/**
 * Trim an AI-generated voiceover script down to a spoken-length budget.
 *
 * The script writer is prompted with a word budget, but LLMs don't reliably
 * self-limit, and this text drives a fixed-length avatar video — an over-long
 * script pushes the narration past the clip and makes caption pacing drift. This
 * is the backstop for when the model ignores the budget outright.
 *
 *  - Only a script that overshoots `maxWords` by more than `ENGAGE_TOLERANCE` is
 *    touched. A near-miss (a few words over) is left alone — trimming there would
 *    drop the closing line (often the CTA) for a sub-second timing gain.
 *  - When it does engage, whole trailing sentences are removed until it fits —
 *    never a mid-sentence cut.
 *  - It never trims below ~65% of the budget: a script that can't fit without
 *    being gutted is returned unchanged for the user to edit rather than butchered.
 *  - Paragraph breaks in the kept portion are preserved.
 *
 * Assumes plain-prose input (the script-writer prompt forbids markdown / labels).
 * Sentence splitting is punctuation-only, so an abbreviation or decimal
 * ("Dr. Smith", "$9.99") counts as a sentence end — acceptable for a coarse
 * length backstop.
 */
const ENGAGE_TOLERANCE = 1.15;

export function fitScriptToWordBudget(script: string, maxWords: number): string {
  const text = (script || '').trim();
  if (!text || maxWords <= 0) return text;

  const countWords = (s: string): number => (s.trim().match(/\S+/g) || []).length;
  if (countWords(text) <= Math.round(maxWords * ENGAGE_TOLERANCE)) return text;

  // Sentence-ish chunks: run of non-terminators, optional terminator(s) + closing
  // quote/bracket, plus the trailing whitespace so re-joining keeps line breaks.
  const sentences = text.match(/[^.!?…]+(?:[.!?…]+["')\]]*)?\s*/g) || [text];
  if (sentences.length <= 1) return text; // nothing to drop without a mid-sentence cut

  const floor = Math.round(maxWords * 0.65);
  let kept = '';
  let keptWords = 0;

  for (const sentence of sentences) {
    const next = keptWords + countWords(sentence);
    if (keptWords >= floor && next > maxWords) break;
    kept += sentence;
    keptWords = next;
  }

  const trimmed = kept.trim();
  if (!trimmed) return text;

  const trimmedWords = countWords(trimmed);
  // Couldn't get under the cap without a mid-sentence cut, or trimming would gut the
  // script — hand back the original for the user to edit rather than mangle it.
  if (trimmedWords > maxWords || trimmedWords < floor) return text;
  return trimmed;
}
